import { Box, Stack, Typography } from '@mui/material';
import { useTheme } from '@mui/system';
import { Point } from 'highcharts';
import { get, isEmpty, isNil, lowerCase, map, pickBy } from 'lodash';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import FlagCountry from 'src/components/common/FlagCountry';
import TooltipFormLabel from 'src/components/common/TooltipFormLabel';
import TooltipLogisticCompany from 'src/components/common/TooltipLogisticCompany';
import ExportDataTable from 'src/components/ExportData';
import { EXPORT_TABLE_TYPE, URL_EXPORT_MAPPING } from 'src/components/ExportData/config';
import TreemapChart from 'src/components/HighChart/Treemap';
import { TREEMAP_PALLET_COLOR } from 'src/components/HighChart/Treemap/config';
import WorldMapChart from 'src/components/HighChart/WorldMapChart';
import { WorldMapOptions } from 'src/components/HighChart/WorldMapChart/config';
import LinkToCompanyDetail from 'src/components/LinkToCompanyDetail';
import CustomTable from 'src/components/Table';
import CompanyTradeDataWhiteBox from 'src/features/CompanyTradeData/common/CompanyTradeDataWhiteBox';
import DataGuard from 'src/features/CompanyTradeData/common/DataGuard';
import FilterChart from 'src/features/CompanyTradeData/common/FilterChart';
import { useQueryParams } from 'src/hooks/useQueryParam';
import {
  useQueryCTDCompetitorChart,
  useQueryCTDCompetitorList,
  useQueryCTDCompetitorWorldMap,
} from 'src/services/queries/useQueryTradeDataWithoutHSCode';
import { KeyFilterChartTradeDataUnitBy, TypeBuyerSupplier } from 'src/utils/constants/enum';
import { LIST_FEATURES } from 'src/utils/constants/featuresPoint';
import getEmojiFlag from 'src/utils/helpers/emojiFlag';
import { checkCompanyLogistics, renderNumberOrDefault, renderValueNumberTrade } from 'src/utils/helpers/stringHelpers';
import { formatValueUSD } from 'src/utils/helpers/utilities';
import { useTradeDataSortStore } from 'src/zustand/useTradeDataSortStore';

const CompanyTradeDataCompetitor = () => {
  const { setSort, pooSortParams } = useTradeDataSortStore();

  const unitFieldMapping = {
    bol: {
      field: 'bol',
      label: 'Number of B/L',
    },
    weight: {
      field: 'weight',
      label: 'Weight',
    },
    quantity: { field: 'quantity', label: 'Quantity' },
    value_usd: { field: 'value_usd', label: 'USD' },
  };
  const worldMapFieldNameMapping = {
    [TypeBuyerSupplier.supplier]: {
      countryCodeField: 'supplier_country_code',
      valueField: 'suppliers',
    },
    [TypeBuyerSupplier.buyer]: {
      countryCodeField: 'buyer_country_code',
      valueField: 'buyers',
    },
  };

  const treemapFieldNameMapping = {
    [TypeBuyerSupplier.supplier]: {
      name: 'company_name',
      countryCodeField: 'company_country_code',
    },
    [TypeBuyerSupplier.buyer]: {
      name: 'company_name',
      countryCodeField: 'company_country_code',
    },
  };
  const tableFieldNameMapping = {
    [TypeBuyerSupplier.supplier]: {
      countryCode: 'company_country_code',
      companyId: 'company_id',
      companyName: 'company_name',
    },
    [TypeBuyerSupplier.buyer]: {
      countryCode: 'company_country_code',
      companyId: 'company_id',
      companyName: 'company_name',
    },
  };

  const isBuyer = false;
  const queryParams = useQueryParams();
  const role = isBuyer ? TypeBuyerSupplier.buyer : TypeBuyerSupplier.supplier;
  const { countryCodeField, valueField } = worldMapFieldNameMapping[role];
  const { name: treemapNameField, countryCodeField: treeMapCountryCodeField } = treemapFieldNameMapping[role];
  const { companyId, companyName, countryCode: tableCountryCodeFieldName } = tableFieldNameMapping[role];
  const {
    palette: { text, secondary },
  } = useTheme();
  const [unitFilter, setUnitFilter] = useState<KeyFilterChartTradeDataUnitBy>(KeyFilterChartTradeDataUnitBy.value_usd);
  const [selectedRangeTime, setSelectedRangeTime] = useState({
    fromTime: queryParams.start_date,
    toTime: queryParams.end_date,
  } as {
    fromTime: string;
    toTime: string;
  });
  const { t } = useTranslation();
  const [proportionTablePaging, setProportionTablePaging] = useState({
    page: 0,
    page_size: 50,
  });
  const chartLabelByUnitFilter = unitFieldMapping[unitFilter].label;
  const chartFieldNameByUnitFilter = unitFieldMapping[unitFilter].field;
  const supplierBuyerCountryRequestPayload = useMemo(() => {
    return pickBy(
      {
        ...queryParams,
        start_date: selectedRangeTime.fromTime,
        end_date: selectedRangeTime.toTime,
        keyword: queryParams.keyword,
      },
      (value) => !isNil(value) && value !== '',
    );
  }, [queryParams, selectedRangeTime]);

  const supplierBuyerCountryListRequestPayload = useMemo(() => {
    return {
      ...supplierBuyerCountryRequestPayload,
      page: proportionTablePaging.page + 1,
      page_size: proportionTablePaging.page_size,
      sort_by: pooSortParams?.sortBy,
      order: pooSortParams?.sort,
    };
  }, [proportionTablePaging, pooSortParams, supplierBuyerCountryRequestPayload]);
  const supplierBuyerCountryChartRequestPayload = useMemo(() => {
    return {
      ...supplierBuyerCountryRequestPayload,
      limit: 10,
    };
  }, [proportionTablePaging, pooSortParams, supplierBuyerCountryRequestPayload]);

  const supplierBuyerCountryWorldMapResp = useQueryCTDCompetitorWorldMap(supplierBuyerCountryRequestPayload);
  const supplierBuyerCountryChartMapResp = useQueryCTDCompetitorChart(supplierBuyerCountryChartRequestPayload);
  const supplierBuyerCountryListResp = useQueryCTDCompetitorList(supplierBuyerCountryListRequestPayload);

  const worldMapChart = useMemo(() => {
    const data = map(supplierBuyerCountryWorldMapResp?.data?.data || [], (item) => {
      return [lowerCase(get(item, countryCodeField, '')), get(item, valueField, 0)];
    }) as Array<[string, number | null]>;
    return (
      <DataGuard
        shouldRenderEmpty={() =>
          !supplierBuyerCountryWorldMapResp.isFetching && isEmpty(supplierBuyerCountryWorldMapResp?.data?.data)
        }
      >
        <WorldMapChart
          tooltipFormatter={function () {
            const point: Point = this.point;
            const flag = getEmojiFlag(point.properties['hc-key']);
            return `<div>
            <b>${flag} ${point.name}</b>
            <br/><b style="color: ${text.secondary}">${queryParams.tab} </b> <b>${renderNumberOrDefault(point.value)}</b>
          </div>`;
          }}
          data={data}
          isLoading={supplierBuyerCountryWorldMapResp.isFetching}
          height={580}
          type={WorldMapOptions.customer}
        />
      </DataGuard>
    );
  }, [JSON.stringify(supplierBuyerCountryWorldMapResp)]);

  const treemapChart = useMemo(() => {
    const data = map(supplierBuyerCountryChartMapResp?.data?.data || [], (item, i) => {
      return {
        name: get(item, treemapNameField, 'N/A'),
        value: Math.cbrt(get(item, `percentage_${chartFieldNameByUnitFilter}`, 0)),
        sortIndex: i,
        countryCode: get(item, treeMapCountryCodeField, ''),
        percentage: get(item, `percentage_${chartFieldNameByUnitFilter}`, 0),
        ...item,
      };
    });
    return (
      <DataGuard
        shouldRenderEmpty={() =>
          !supplierBuyerCountryChartMapResp.isFetching && isEmpty(supplierBuyerCountryChartMapResp?.data?.data)
        }
      >
        <TreemapChart
          loading={supplierBuyerCountryChartMapResp.isFetching}
          data={data}
          dataLabelFormatter={function () {
            const point: Point = this.point;
            const flag = getEmojiFlag(get(point, 'countryCode', '')?.toLowerCase());
            return `<div style="color: ${secondary.main}; font-weight: 500; font-size: 14px">
              <span style="font-size:24px">${flag}</span> ${point.name}<br/>
            </div>`;
          }}
          tooltipFormatter={function () {
            const point: Point = this.point;
            const flag = getEmojiFlag(get(point, 'countryCode', '')?.toLowerCase());
            const value = get(point.options, chartFieldNameByUnitFilter);
            return `
              <div style="min-width: 270px">
                <span style="font-size: 24px; color: inherit; display: block; color: inherit; fontWeight: 600; margin-bottom: 5px">
                  ${point.percentage}%
                </span>
                <span style="color: inherit; display: block; color: inherit; fontWeight: 600">
                ${flag} ${point.name}
                </span>
                <span style="color: inherit; display: block; color: ${text.secondary}">${chartLabelByUnitFilter} <b style="color: #fff">
               ${renderNumberOrDefault(value)}</b>
                </span>
              </div>`;
          }}
          height={620}
          colors={TREEMAP_PALLET_COLOR}
        />
      </DataGuard>
    );
  }, [JSON.stringify(supplierBuyerCountryChartMapResp), unitFilter]);

  const proportionTableConfigColumn: ConfigCell<ICompanyAggregatedByBOL>[] = [
    {
      disablePadding: false,
      id: isBuyer ? 'buyer_name' : 'supplier_name',
      label: 'Competitors',
      cell: (data: ICompanyAggregatedByBOL) => {
        return (
          <Stack direction="row" spacing={1}>
            <FlagCountry code={get(data, tableCountryCodeFieldName)} />
            <LinkToCompanyDetail
              id={get(data, companyId)}
              companyName={get(data, companyName)}
              params={queryParams}
              viewedAsType={isBuyer ? TypeBuyerSupplier.buyer : TypeBuyerSupplier.supplier}
            />
            {checkCompanyLogistics(get(data, companyName)) && <TooltipLogisticCompany />}
          </Stack>
        );
      },
    },
    {
      disablePadding: false,
      id: 'bol',
      label: t('trade_data.port_origin.table.shipments'),
      cell: (data: ICompanyAggregatedByBOL) => (
        <Typography variant="body2" color="text.secondary">
          {renderNumberOrDefault(data.bol)}
        </Typography>
      ),
    },
    {
      disablePadding: false,
      id: 'weight',
      label: t('trade_data.port_origin.table.weight'),
      cell: (data: ICompanyAggregatedByBOL) => (
        <Typography variant="body2" color="text.secondary">
          {renderValueNumberTrade(data.weight)}
        </Typography>
      ),
    },
    {
      disablePadding: false,
      id: 'quantity',
      label: t('trade_data.port_origin.table.teu'),
      width: 150,
      cell: (data: ICompanyAggregatedByBOL) => (
        <Typography variant="body2" color="text.secondary">
          {renderValueNumberTrade(data.quantity)}
        </Typography>
      ),
    },
    {
      disablePadding: false,
      id: 'value_usd',
      label: (
        <Stack direction={'row'} gap={0.5} alignItems={'center'}>
          <Box>{t('market_analytics.table.column_name.value')}</Box>
          <TooltipFormLabel title={t('trade_data.value_usd.disclaimer.tooltips')} />
        </Stack>
      ),
      cell: (data: ICompanyAggregatedByBOL) => (
        <Typography variant="body2" color="text.secondary">
          {formatValueUSD(data.value_usd)}
        </Typography>
      ),
    },
  ];

  return (
    <>
      <CompanyTradeDataWhiteBox>
        <Typography variant="h5">
          Where Are {queryParams.tab} of{' '}
          <Box component={'span'} color="secondary.main">
            {queryParams?.keyword}{' '}
          </Box>
          Located?
        </Typography>
        {worldMapChart}
      </CompanyTradeDataWhiteBox>

      <CompanyTradeDataWhiteBox>
        <FilterChart
          selectedUnitBy={unitFilter}
          onSelectUnitBy={(unit) => {
            setUnitFilter(unit as any);
          }}
          selectedRangeTime={selectedRangeTime}
          onSelectTimeRange={({ fromTime, toTime }) => {
            setSelectedRangeTime({
              fromTime: fromTime,
              toTime: toTime,
            });
          }}
          title={'Most Recent Bill of Lading'}
        />
        {treemapChart}
      </CompanyTradeDataWhiteBox>

      <CompanyTradeDataWhiteBox>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="h5">{queryParams.tab} Data List</Typography>
          <ExportDataTable
            fileName="ctd-competitors"
            featureId={LIST_FEATURES.COMPANY_PROFILE_COMPETITORS}
            endpoint={get(URL_EXPORT_MAPPING, EXPORT_TABLE_TYPE.CTD_COMPETITOR)}
            paramsExport={supplierBuyerCountryListRequestPayload}
            totalRecordsAvailable={supplierBuyerCountryListResp.data?.total_records || 0}
          />
        </Stack>
        <CustomTable
          handleSort={(data) => {
            setSort('pooSortParams', data);
          }}
          maxRow={50}
          onChangePage={(value) => {
            setProportionTablePaging({ ...proportionTablePaging, page: value });
          }}
          onChangePageSize={(value) => setProportionTablePaging({ page: 0, page_size: value })}
          loading={supplierBuyerCountryListResp.isFetching}
          page={proportionTablePaging.page}
          pageSize={proportionTablePaging.page_size}
          totalRecords={supplierBuyerCountryListResp.data?.total_records}
          dataKey={isBuyer ? 'buyer_id' : 'supplier_id'}
          data={supplierBuyerCountryListResp.data?.data ?? []}
          configColumn={proportionTableConfigColumn}
        />
      </CompanyTradeDataWhiteBox>
    </>
  );
};

export default CompanyTradeDataCompetitor;
